import { Trans } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { useQueryHelper } from '@anytag/shared-hooks';
import { UNEXPECTED_ERROR } from '@anytag/shared-constants';
import { tiktokCreateFailedImage } from '@anytag/shared-assets';
import { BaseModal, Icon, MediaPreview } from '@anytag/shared-atoms';
import { getTikTokCampaignFormInput, defaultTikTokCampaignFormValues } from '@anytag/shared-organisms';
import { usePricingTableQuery, PaymentPlanType, useCreateTiktokSpecialCampaignMutation } from '@anytag/graphql';

interface CreateCampaignFailedModalProps {
  onRefetchCampaignsList: () => void;
}

export const CreateCampaignFailedModal = ({ onRefetchCampaignsList }: CreateCampaignFailedModalProps) => {
  const { isAdvertiserFromTikTok } = useUserRoles();
  const { t, navigate, enqueueSnackbar } = useQueryHelper();
  const { called, error, callCreateTiktokSpecialCampaign } = useCreateTiktokSpecialCampaignMutation();
  const { data, loading, refetch } = usePricingTableQuery({
    fetchPolicy: 'no-cache', // Must no cache to get the newest plans
    skip: !isAdvertiserFromTikTok,
    variables: { planType: PaymentPlanType.MARKETPLACE }
  });

  // This modal will be shown if user purchased a plan but failed to create a campaign
  // So user must complete the campaign creation step before do other actions
  const plans = data?.pricingTable?.plans || [];
  const hasCurrentPlan = !!plans.length && data?.pricingTable?.plans.findIndex((plan) => plan.isCurrentPlan) !== -1;
  const isCreating = called && !error && hasCurrentPlan; // loading combined both create campaign and refetch pricing table

  const handleRecreateCampaign = async () => {
    const draftCampaignTitle = 'New Campaign'; // hardcode

    try {
      const { data } = await callCreateTiktokSpecialCampaign({
        variables: {
          input: getTikTokCampaignFormInput({
            ...defaultTikTokCampaignFormValues,
            isDraft: true,
            title: draftCampaignTitle
          })
        }
      });
      const createdCampaignId = data?.createTiktokSpecialCampaign?.id;

      if (createdCampaignId) {
        navigate({ to: '/tiktok/campaigns/$id', params: { id: createdCampaignId.toString() } });
      } else {
        refetch(); // close modal
        onRefetchCampaignsList();
      }
      enqueueSnackbar(t('succeededInCreating', { name: draftCampaignTitle }), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error || UNEXPECTED_ERROR), { variant: 'error' });
    }
  };

  return (
    <BaseModal
      open={isAdvertiserFromTikTok && !loading && hasCurrentPlan}
      footer={{
        submitButton: {
          onClick: handleRecreateCampaign,
          loading: { showIcon: true, status: isCreating },
          title: t(isCreating ? 'Button.Recreating' : 'Button.Recreate')
        }
      }}
    >
      <MediaPreview
        src={tiktokCreateFailedImage}
        className="mx-auto my-0 h-43.5 w-96.5 content-center rounded-lg bg-light-gray"
      />
      <div className="mx-auto mt-2 flex size-4 items-center justify-center rounded-full bg-black-90">
        <Icon icon="link-broken" size={8} variant="white" />
      </div>
      <p className="mt-2 text-center text-lg leading-none font-semibold">{t('Modal.Campaign Create Failed')}</p>
      <p className="mt-2 text-center text-sm leading-none text-tertiary">
        <Trans i18nKey="Modal.campaignCreateFailedDecription" components={{ br: <br /> }} />
      </p>
    </BaseModal>
  );
};
